import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, LinearProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ReplayIcon from '@mui/icons-material/Replay';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MovieIcon from '@mui/icons-material/Movie';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VideoScene {
  id: number;
  type: 'title' | 'explanation' | 'bullets' | 'highlight' | 'diagram';
  text: string;
  voice?: string;
  animation: 'fadeIn' | 'slideUp' | 'slideLeft' | 'slideRight' | 'zoomIn' | 'stagger';
  duration: number;
  accent?: string;
  icon?: string;
}

interface Props {
  scenes: VideoScene[];
  title?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BG_GRADIENTS = [
  'linear-gradient(135deg,#0d0d1f 0%,#1a0a2e 100%)',
  'linear-gradient(135deg,#051015 0%,#0a1e2a 100%)',
  'linear-gradient(135deg,#050a10 0%,#0d1a10 100%)',
  'linear-gradient(135deg,#0a0500 0%,#1a0e00 100%)',
  'linear-gradient(135deg,#050510 0%,#0a0a20 100%)',
  'linear-gradient(135deg,#030a08 0%,#071410 100%)',
  'linear-gradient(135deg,#100a00 0%,#1a1000 100%)',
];

const ACCENT_COLORS = ['#7C3AED', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899'];

const TYPE_ICONS: Record<VideoScene['type'], string> = {
  title: '🎬',
  explanation: '💡',
  bullets: '📋',
  highlight: '⚡',
  diagram: '🔗',
};

const HIGHLIGHT_KEYWORDS = [
  'photosynthesis', 'glucose', 'oxygen', 'chlorophyll', 'carbon dioxide', 'water',
  'energy', 'sunlight', 'reaction', 'process', 'molecule', 'cell', 'atom', 'force',
  'gravity', 'velocity', 'acceleration', 'momentum', 'newton', 'electron', 'proton',
  'nucleus', 'dna', 'rna', 'protein', 'enzyme', 'mitosis', 'meiosis', 'ecosystem',
  'evolution', 'natural selection', 'frequency', 'wavelength', 'amplitude',
  'current', 'voltage', 'resistance', 'ohm', 'circuit', 'magnetic', 'electric',
  'derivative', 'integral', 'function', 'equation', 'theorem', 'proof', 'matrix',
  'temperature', 'pressure', 'density', 'chemical', 'biology', 'physics', 'chemistry',
  'history', 'geography', 'democracy', 'revolution', 'civilization', 'constitution',
];

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const formatTime = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

const highlightKeywords = (text: string, accentColor: string): React.ReactNode => {
  if (!text) return text;
  const escaped = HIGHLIGHT_KEYWORDS.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part) ? (
      <span key={i} style={{ color: accentColor, fontWeight: 600, borderBottom: `1.5px solid ${accentColor}55`, paddingBottom: 1 }}>
        {part}
      </span>
    ) : part
  );
};

const estimateSpeechMs = (text: string): number => {
  const words = (text || '').trim().split(/\s+/).length;
  return Math.max(2500, (words / 140) * 60 * 1000);
};

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------

const KEYFRAMES = `
  @keyframes vpFadeIn    { from{opacity:0;transform:scale(0.96)} to{opacity:1;transform:scale(1)} }
  @keyframes vpSlideUp   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
  @keyframes vpSlideLeft { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes vpSlideRight{ from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }
  @keyframes vpZoomIn    { from{opacity:0;transform:scale(0.78)} to{opacity:1;transform:scale(1)} }
  @keyframes vpBulletIn  { from{opacity:0;transform:translateX(-14px)} to{opacity:1;transform:translateX(0)} }
  @keyframes vpIconBounce{ 0%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} 70%{transform:translateY(2px)} }
  @keyframes vpSpeakBar  { 0%,100%{height:3px} 50%{height:14px} }
  @keyframes vpParticle0 { 0%,100%{transform:translate(0,0) rotate(0deg)}   33%{transform:translate(8px,-14px) rotate(70deg)}  66%{transform:translate(-7px,9px) rotate(140deg)} }
  @keyframes vpParticle1 { 0%,100%{transform:translate(0,0) rotate(0deg)}   33%{transform:translate(-11px,9px) rotate(-50deg)} 66%{transform:translate(13px,-7px) rotate(35deg)} }
  @keyframes vpParticle2 { 0%,100%{transform:translate(0,0) rotate(0deg)}   50%{transform:translate(7px,16px) rotate(100deg)} }
  @keyframes vpPulse     { 0%,100%{opacity:0.05} 50%{opacity:0.18} }
  @keyframes vpFSControlsIn { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
`;

const ANIM_MAP: Record<string, string> = {
  fadeIn:     'vpFadeIn     0.55s ease forwards',
  slideUp:    'vpSlideUp    0.5s  ease forwards',
  slideLeft:  'vpSlideLeft  0.5s  ease forwards',
  slideRight: 'vpSlideRight 0.5s  ease forwards',
  zoomIn:     'vpZoomIn     0.6s  cubic-bezier(0.34,1.56,0.64,1) forwards',
  stagger:    'vpFadeIn     0.45s ease forwards',
};

// ---------------------------------------------------------------------------
// Particles
// ---------------------------------------------------------------------------

interface Particle {
  id: number; x: number; y: number; size: number; opacity: number;
  shape: 'circle' | 'square' | 'tri'; animIdx: number; dur: number; delay: number;
}

const PARTICLES: Particle[] = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  x: (i * 13 + 7) % 97,
  y: (i * 17 + 5) % 93,
  size: 2 + (i % 4) * 1.2,
  opacity: 0.04 + (i % 4) * 0.025,
  shape: (['circle', 'square', 'tri'] as const)[i % 3],
  animIdx: i % 3,
  dur: 7 + (i % 6) * 1.8,
  delay: -(i * 0.75),
}));

const ParticlesLayer: React.FC<{ accent: string; running: boolean }> = ({ accent, running }) => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {PARTICLES.map(p => {
      const animName = `vpParticle${p.animIdx}`;
      const anim = running
        ? `${animName} ${p.dur}s ${p.delay}s ease-in-out infinite, vpPulse ${p.dur * 1.4}s ${p.delay}s ease-in-out infinite`
        : 'none';
      const base: React.CSSProperties = {
        position: 'absolute', left: `${p.x}%`, top: `${p.y}%`,
        opacity: p.opacity, animation: anim,
      };
      if (p.shape === 'circle')
        return <Box key={p.id} sx={{ ...base, width: p.size, height: p.size, borderRadius: '50%', background: accent }} />;
      if (p.shape === 'square')
        return <Box key={p.id} sx={{ ...base, width: p.size * 0.9, height: p.size * 0.9, border: `1px solid ${accent}` }} />;
      return (
        <Box key={p.id} sx={{
          ...base, width: 0, height: 0,
          borderLeft: `${p.size * 0.7}px solid transparent`,
          borderRight: `${p.size * 0.7}px solid transparent`,
          borderBottom: `${p.size * 1.3}px solid ${accent}`,
        }} />
      );
    })}
  </Box>
);

// ---------------------------------------------------------------------------
// Speaking wave
// ---------------------------------------------------------------------------

const SpeakWave: React.FC<{ accent: string; size?: 'sm' | 'lg' }> = ({ accent, size = 'sm' }) => {
  const h = size === 'lg' ? 20 : 16;
  const w = size === 'lg' ? 4 : 3;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', height: h, mt: 1 }}>
      {[0, 1, 2, 3, 4].map(i => (
        <Box key={i} sx={{ width: w, borderRadius: 2, background: accent, animation: `vpSpeakBar 0.75s ${i * 0.1}s ease-in-out infinite` }} />
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Scene content — scales based on isFullscreen
// ---------------------------------------------------------------------------

const SceneContent: React.FC<{
  scene: VideoScene;
  accent: string;
  bg: string;
  animKey: number;
  isSpeaking: boolean;
  isFullscreen: boolean;
}> = ({ scene, accent, bg, animKey, isSpeaking, isFullscreen }) => {
  const icon = scene.icon || TYPE_ICONS[scene.type] || '📖';
  const entryAnim = ANIM_MAP[scene.animation] || ANIM_MAP.fadeIn;

  const fs = isFullscreen;
  const titleSize  = fs ? 40  : 20;
  const bodySize   = fs ? 20  : 13;
  const smallSize  = fs ? 13  : 9;
  const iconSize   = fs ? 50  : 28;
  const bulletSize = fs ? 17  : 12;
  const dotSize    = fs ? 26  : 18;
  const dotFont    = fs ? 11  : 8;
  const maxWidth   = fs ? 680 : 360;
  const padH       = fs ? '48px 44px' : '20px 18px';

  const wrap: React.CSSProperties = {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: padH, background: bg,
  };

  const IconEl = (
    <div style={{ fontSize: iconSize, marginBottom: fs ? 16 : 8, lineHeight: 1, animation: 'vpIconBounce 0.7s 0.25s ease both', display: 'inline-block' }}>
      {icon}
    </div>
  );

  if (scene.type === 'title') return (
    <div style={wrap} key={animKey}>
      <div style={{ textAlign: 'center', animation: entryAnim }}>
        {IconEl}
        <div style={{ fontSize: smallSize, letterSpacing: 3, textTransform: 'uppercase', color: accent, marginBottom: fs ? 14 : 8, fontWeight: 600 }}>
          LESSON {scene.id}
        </div>
        <div style={{ fontSize: titleSize, fontWeight: 700, color: '#fff', lineHeight: 1.25, marginBottom: fs ? 16 : 8 }}>
          {scene.text}
        </div>
        {scene.voice && (
          <div style={{ fontSize: fs ? 15 : 11, color: 'rgba(255,255,255,0.45)', maxWidth }}>{scene.voice}</div>
        )}
        {isSpeaking && <SpeakWave accent={accent} size={fs ? 'lg' : 'sm'} />}
      </div>
    </div>
  );

  if (scene.type === 'explanation') return (
    <div style={wrap} key={animKey}>
      <div style={{ textAlign: 'center', maxWidth, animation: entryAnim }}>
        {IconEl}
        <div style={{ width: 28, height: 2.5, background: accent, borderRadius: 2, margin: `0 auto ${fs ? 18 : 12}px` }} />
        <div style={{ fontSize: bodySize, lineHeight: 1.85, color: 'rgba(255,255,255,0.88)' }}>
          {highlightKeywords(scene.text, accent)}
        </div>
        {isSpeaking && <SpeakWave accent={accent} size={fs ? 'lg' : 'sm'} />}
      </div>
    </div>
  );

  if (scene.type === 'bullets') {
    const parts = scene.text.split('|');
    const heading = parts[0];
    const items = parts.slice(1);
    return (
      <div style={wrap} key={animKey}>
        <div style={{ width: '100%', maxWidth, animation: entryAnim }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: fs ? 12 : 8, marginBottom: fs ? 20 : 13 }}>
            <span style={{ fontSize: fs ? 34 : 22 }}>{icon}</span>
            <div style={{ fontSize: fs ? 20 : 14, fontWeight: 700, color: '#fff' }}>{heading}</div>
          </div>
          {items.map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: fs ? 12 : 8, marginBottom: fs ? 12 : 8,
              opacity: 0, animation: `vpBulletIn 0.4s ${0.1 + i * 0.13}s ease forwards`,
            }}>
              <div style={{
                width: dotSize, height: dotSize, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                background: `${accent}1a`, border: `1px solid ${accent}55`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: dotFont, color: accent, fontWeight: 700,
              }}>{i + 1}</div>
              <span style={{ fontSize: bulletSize, color: 'rgba(255,255,255,0.88)', lineHeight: 1.7 }}>
                {highlightKeywords(item, accent)}
              </span>
            </div>
          ))}
          {isSpeaking && <div style={{ marginTop: 4 }}><SpeakWave accent={accent} size={fs ? 'lg' : 'sm'} /></div>}
        </div>
      </div>
    );
  }

  if (scene.type === 'highlight') return (
    <div style={wrap} key={animKey}>
      <div style={{ textAlign: 'center', animation: entryAnim, padding: '0 10px' }}>
        {IconEl}
        <div style={{
          fontSize: fs ? 20 : 14, fontWeight: 600, color: '#fff', lineHeight: 1.6,
          padding: fs ? '26px 34px' : '14px 18px', borderRadius: 12,
          background: `${accent}18`, border: `1px solid ${accent}55`,
          boxShadow: isSpeaking ? `0 0 28px ${accent}44` : `0 0 18px ${accent}18`,
          transition: 'box-shadow 0.4s', maxWidth,
        }}>
          {highlightKeywords(scene.text, accent)}
        </div>
        {isSpeaking && <div style={{ marginTop: 8 }}><SpeakWave accent={accent} size={fs ? 'lg' : 'sm'} /></div>}
      </div>
    </div>
  );

  if (scene.type === 'diagram') {
    const nodes = scene.text.split('|');
    const isGrid = nodes.length > 4;
    return (
      <div style={wrap} key={animKey}>
        <div style={{ animation: entryAnim, width: '100%', maxWidth }}>
          <div style={{ textAlign: 'center', marginBottom: fs ? 16 : 10 }}>
            <span style={{ fontSize: fs ? 34 : 22 }}>{icon}</span>
          </div>
          {isGrid ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: fs ? 10 : 6 }}>
              {nodes.map((node, i) => (
                <div key={i} style={{
                  padding: fs ? '10px 16px' : '7px 10px', borderRadius: 8,
                  fontSize: fs ? 15 : 11, fontWeight: 500,
                  background: `${accent}18`, border: `1px solid ${accent}44`,
                  color: '#fff', textAlign: 'center',
                  opacity: 0, animation: `vpBulletIn 0.35s ${i * 0.1}s ease forwards`,
                }}>{node}</div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: fs ? 5 : 3 }}>
              {nodes.map((node, i) => (
                <React.Fragment key={i}>
                  <div style={{
                    padding: fs ? '10px 30px' : '7px 20px', borderRadius: 8,
                    fontSize: fs ? 15 : 12, fontWeight: 500,
                    background: `${accent}18`, border: `1px solid ${accent}44`,
                    color: '#fff', textAlign: 'center', minWidth: fs ? 190 : 120,
                    opacity: 0, animation: `vpBulletIn 0.35s ${i * 0.12}s ease forwards`,
                  }}>{node}</div>
                  {i < nodes.length - 1 && (
                    <div style={{ color: `${accent}88`, fontSize: fs ? 18 : 13, lineHeight: 1 }}>↓</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
          {isSpeaking && <div style={{ marginTop: 8 }}><SpeakWave accent={accent} size={fs ? 'lg' : 'sm'} /></div>}
        </div>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// Web Speech hook
// ---------------------------------------------------------------------------

const useSpeech = () => {
  const synth = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  const supported = Boolean(synth);
  const stop   = useCallback(() => { synth?.cancel(); }, [synth]);
  const pause  = useCallback(() => synth?.pause(), [synth]);
  const resume = useCallback(() => synth?.resume(), [synth]);
  const speak  = useCallback((text: string, onStart?: () => void, onEnd?: () => void) => {
    if (!synth || !text.trim()) { onEnd?.(); return; }
    synth.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.93; utt.pitch = 1; utt.volume = 1;
    const setVoice = () => {
      const voices = synth.getVoices();
      const preferred = voices.find(v => v.lang.startsWith('en-') && v.localService) || voices.find(v => v.lang.startsWith('en')) || voices[0];
      if (preferred) utt.voice = preferred;
    };
    setVoice();
    if (synth.getVoices().length === 0) {
      speechSynthesis.onvoiceschanged = () => { setVoice(); speechSynthesis.onvoiceschanged = null; };
    }
    utt.onstart = () => onStart?.();
    utt.onend   = () => onEnd?.();
    utt.onerror = () => onEnd?.();
    synth.speak(utt);
  }, [synth]);
  return { speak, stop, pause, resume, supported };
};

// ---------------------------------------------------------------------------
// YouTube-style fullscreen overlay controls
// ---------------------------------------------------------------------------

const FullscreenOverlay: React.FC<{
  visible: boolean;
  scenes: VideoScene[];
  currentIdx: number;
  isPlaying: boolean;
  muted: boolean;
  isSpeaking: boolean;
  sceneProgress: number;
  globalElapsed: number;
  totalDur: number;
  accent: string;
  title?: string;
  onTogglePlay: () => void;
  onPrev: () => void;
  onNext: () => void;
  onMute: () => void;
  onRestart: () => void;
  onExitFullscreen: () => void;
  onSceneClick: (i: number) => void;
}> = ({
  visible, scenes, currentIdx, isPlaying, muted, isSpeaking,
  sceneProgress, globalElapsed, totalDur, accent, title,
  onTogglePlay, onPrev, onNext, onMute, onRestart, onExitFullscreen, onSceneClick,
}) => {
  const btnSx = {
    color: '#fff',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '50%' },
    transition: 'background 0.15s',
    p: '8px',
  };

  return (
    <Box sx={{
      position: 'absolute', inset: 0, zIndex: 20,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      background: visible
        ? 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 28%, transparent 65%, rgba(0,0,0,0.75) 100%)'
        : 'transparent',
      opacity: visible ? 1 : 0,
      transition: 'opacity 0.3s ease',
      pointerEvents: visible ? 'auto' : 'none',
      animation: visible ? 'vpFSControlsIn 0.25s ease' : 'none',
    }}>
      {/* Top bar — title */}
      <Box sx={{ px: 3, pt: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MovieIcon sx={{ fontSize: 16, color: accent }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: 0.3 }}>
          {title || 'Video Lesson'}
        </Typography>
        {isSpeaking && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', ml: 1.5, height: 16 }}>
            {[0, 1, 2, 3].map(i => (
              <Box key={i} sx={{ width: 3, borderRadius: 2, background: accent, animation: `vpSpeakBar 0.7s ${i * 0.1}s ease-in-out infinite` }} />
            ))}
          </Box>
        )}
      </Box>

      {/* Bottom controls */}
      <Box sx={{ px: 2, pb: 2 }}>
        {/* Scene dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '6px', mb: 1.5 }}>
          {scenes.map((_, i) => (
            <Box
              key={i}
              onClick={() => onSceneClick(i)}
              sx={{
                width: i === currentIdx ? 24 : 7,
                height: 7, borderRadius: 4, cursor: 'pointer',
                bgcolor: i === currentIdx ? accent : i < currentIdx ? `${accent}66` : 'rgba(255,255,255,0.25)',
                transition: 'all 0.22s ease',
                '&:hover': { bgcolor: i === currentIdx ? accent : 'rgba(255,255,255,0.55)' },
              }}
            />
          ))}
        </Box>

        {/* Progress bar */}
        <Box sx={{ mb: 1.5, px: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={(globalElapsed / Math.max(totalDur, 1)) * 100}
            sx={{
              height: 4, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 2, transition: 'transform 0.08s linear' },
            }}
          />
          {/* Scene-level thin bar */}
          <Box sx={{ mt: 0.5, height: 2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)' }}>
            <Box sx={{ height: '100%', bgcolor: `${accent}88`, borderRadius: 1, width: `${sceneProgress}%`, transition: 'width 0.08s linear' }} />
          </Box>
        </Box>

        {/* Buttons row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Prev */}
          <Tooltip title="Previous (←)" arrow>
            <span>
              <IconButton size="small" onClick={onPrev} disabled={currentIdx === 0} sx={{ ...btnSx, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}>
                <SkipPreviousIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Play/Pause — bigger */}
          <IconButton
            onClick={onTogglePlay}
            sx={{
              bgcolor: accent, color: '#fff', width: 52, height: 52,
              '&:hover': { bgcolor: accent, filter: 'brightness(1.2)' },
              transition: 'filter 0.15s', mx: 0.5,
            }}
          >
            {isPlaying ? <PauseIcon sx={{ fontSize: 30 }} /> : <PlayArrowIcon sx={{ fontSize: 30 }} />}
          </IconButton>

          {/* Next */}
          <Tooltip title="Next (→)" arrow>
            <span>
              <IconButton size="small" onClick={onNext} disabled={currentIdx === scenes.length - 1} sx={{ ...btnSx, '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' } }}>
                <SkipNextIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </span>
          </Tooltip>

          {/* Time */}
          <Typography sx={{ fontSize: 13, fontFamily: 'monospace', color: 'rgba(255,255,255,0.75)', ml: 1, whiteSpace: 'nowrap' }}>
            {formatTime(globalElapsed)} / {formatTime(totalDur)}
          </Typography>

          <Box sx={{ flex: 1 }} />

          {/* Scene count */}
          <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', mr: 0.5, fontFamily: 'monospace' }}>
            {currentIdx + 1}/{scenes.length}
          </Typography>

          {/* Volume / Mute */}
          <Tooltip title={muted ? 'Unmute (M)' : 'Mute (M)'} arrow>
            <IconButton size="small" onClick={onMute} sx={btnSx}>
              {muted ? <VolumeOffIcon sx={{ fontSize: 24 }} /> : <VolumeUpIcon sx={{ fontSize: 24 }} />}
            </IconButton>
          </Tooltip>

          {/* Restart */}
          <Tooltip title="Restart" arrow>
            <IconButton size="small" onClick={onRestart} sx={btnSx}>
              <ReplayIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>

          {/* Exit Fullscreen */}
          <Tooltip title="Exit fullscreen (Esc / F)" arrow>
            <IconButton size="small" onClick={onExitFullscreen} sx={btnSx}>
              <FullscreenExitIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main player
// ---------------------------------------------------------------------------

const AnimatedVideoPlayer: React.FC<Props> = ({ scenes, title }) => {
  const [currentIdx, setCurrentIdx]       = useState(0);
  const [isPlaying, setIsPlaying]         = useState(false);
  const [isSpeaking, setIsSpeaking]       = useState(false);
  const [muted, setMuted]                 = useState(false);
  const [sceneProgress, setSceneProgress] = useState(0);
  const [animKey, setAnimKey]             = useState(0);
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [fsControlsVisible, setFsControlsVisible] = useState(true);

  const containerRef   = useRef<HTMLDivElement>(null);
  const progressRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const doneRef        = useRef(false);
  const prevPlayingRef = useRef(isPlaying);
  const hideTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { speak, stop, pause, resume, supported } = useSpeech();

  const scene    = scenes?.[currentIdx];
  const accent   = scene?.accent ?? ACCENT_COLORS[currentIdx % ACCENT_COLORS.length];
  const bg       = BG_GRADIENTS[currentIdx % BG_GRADIENTS.length];
  const totalDur = scenes.reduce((a, s) => a + s.duration, 0);
  const baseDur  = scenes.slice(0, currentIdx).reduce((a, s) => a + s.duration, 0);
  const globalElapsed = baseDur + (sceneProgress / 100) * (scene?.duration ?? 0);

  // ── Auto-hide fullscreen controls ──
  const showFsControls = useCallback(() => {
    setFsControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setFsControlsVisible(false), 3000);
  }, []);

  // ── Fullscreen API ──
  const enterFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
      else if ((el as any).mozRequestFullScreen) (el as any).mozRequestFullScreen();
    } catch { setIsFullscreen(true); }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
      else if ((document as any).mozCancelFullScreen) (document as any).mozCancelFullScreen();
    } catch { setIsFullscreen(false); }
  }, []);

  useEffect(() => {
    const onChange = () => {
      const fsEl =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement;
      setIsFullscreen(!!fsEl);
      if (!!fsEl) showFsControls();
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    document.addEventListener('mozfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      document.removeEventListener('mozfullscreenchange', onChange);
    };
  }, [showFsControls]);

  // Keyboard shortcuts (active always, not just in fullscreen)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'f' || e.key === 'F') { isFullscreen ? exitFullscreen() : enterFullscreen(); }
      if (e.key === ' ' && isFullscreen) { e.preventDefault(); setIsPlaying(p => !p); }
      if (e.key === 'ArrowRight' && isFullscreen) setCurrentIdx(ci => Math.min(scenes.length - 1, ci + 1));
      if (e.key === 'ArrowLeft'  && isFullscreen) setCurrentIdx(ci => Math.max(0, ci - 1));
      if ((e.key === 'm' || e.key === 'M') && isFullscreen) setMuted(m => !m);
      if (e.key === 'Escape' && isFullscreen) exitFullscreen();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen, enterFullscreen, exitFullscreen, scenes.length]);

  // Mouse move in fullscreen → show controls
  useEffect(() => {
    if (!isFullscreen) return;
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('mousemove', showFsControls);
    el.addEventListener('touchstart', showFsControls);
    return () => {
      el.removeEventListener('mousemove', showFsControls);
      el.removeEventListener('touchstart', showFsControls);
    };
  }, [isFullscreen, showFsControls]);

  // ── Progress timer ──
  const clearProg = useCallback(() => {
    if (progressRef.current) { clearInterval(progressRef.current); progressRef.current = null; }
  }, []);

  const startProg = useCallback((ms: number) => {
    clearProg();
    const tick = 80;
    let elapsed = 0;
    progressRef.current = setInterval(() => {
      elapsed += tick;
      setSceneProgress(Math.min(100, (elapsed / ms) * 100));
      if (elapsed >= ms) clearProg();
    }, tick);
  }, [clearProg]);

  // ── Scene transition ──
  const goToScene = useCallback((idx: number, autoPlay = false) => {
    stop(); clearProg();
    setCurrentIdx(idx);
    setSceneProgress(0);
    setIsSpeaking(false);
    setAnimKey(k => k + 1);
    if (autoPlay) setTimeout(() => setIsPlaying(true), 60);
    else setIsPlaying(false);
  }, [stop, clearProg]);

  // ── Play scene ──
  const playScene = useCallback((s: VideoScene, onDone: () => void) => {
    const voiceText = s.voice || s.text.replace(/\|/g, '. ');
    if (muted || !supported || !voiceText.trim()) {
      startProg(s.duration * 1000);
      const t = setTimeout(onDone, s.duration * 1000);
      return () => clearTimeout(t);
    }
    const ms = estimateSpeechMs(voiceText);
    startProg(ms);
    speak(
      voiceText,
      () => setIsSpeaking(true),
      () => { setIsSpeaking(false); setSceneProgress(100); onDone(); },
    );
    return () => {};
  }, [muted, supported, speak, startProg]);

  // ── Main play effect ──
  useEffect(() => {
    if (!isPlaying || !scene) return;
    doneRef.current = false;
    const cleanup = playScene(scene, () => {
      if (doneRef.current) return;
      doneRef.current = true;
      setCurrentIdx(ci => {
        const next = ci + 1;
        if (next >= scenes.length) { setIsPlaying(false); setSceneProgress(100); return ci; }
        setSceneProgress(0); setIsSpeaking(false); setAnimKey(k => k + 1);
        return next;
      });
    });
    return () => { doneRef.current = true; cleanup?.(); };
  }, [isPlaying, currentIdx]);

  // ── Pause / resume ──
  useEffect(() => {
    if (prevPlayingRef.current === isPlaying) return;
    prevPlayingRef.current = isPlaying;
    if (!isPlaying) { pause(); clearProg(); }
    else resume();
  }, [isPlaying, pause, resume, clearProg]);

  // ── Mute ──
  useEffect(() => {
    if (muted && isSpeaking) { stop(); setIsSpeaking(false); clearProg(); }
  }, [muted, isSpeaking, stop, clearProg]);

  useEffect(() => () => { stop(); clearProg(); }, [stop, clearProg]);

  const togglePlay = () => {
    if (!isPlaying && currentIdx >= scenes.length - 1 && sceneProgress >= 99) { goToScene(0, true); return; }
    setIsPlaying(p => !p);
  };

  if (!scenes?.length) return null;

  // ── Compact mini control bar (shown in chat, NOT in fullscreen) ──
  const MiniControls = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.6, bgcolor: '#08080f',
    }}>
      {/* Prev */}
      <IconButton size="small" onClick={() => goToScene(Math.max(0, currentIdx - 1))} disabled={currentIdx === 0}
        sx={{ color: 'rgba(255,255,255,0.45)', p: '4px', '&:hover': { color: '#fff' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.12)' } }}>
        <SkipPreviousIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Play/Pause */}
      <IconButton onClick={togglePlay} sx={{ bgcolor: accent, color: '#fff', width: 28, height: 28, '&:hover': { bgcolor: accent, filter: 'brightness(1.2)' } }}>
        {isPlaying ? <PauseIcon sx={{ fontSize: 15 }} /> : <PlayArrowIcon sx={{ fontSize: 15 }} />}
      </IconButton>

      {/* Next */}
      <IconButton size="small" onClick={() => goToScene(Math.min(scenes.length - 1, currentIdx + 1))} disabled={currentIdx === scenes.length - 1}
        sx={{ color: 'rgba(255,255,255,0.45)', p: '4px', '&:hover': { color: '#fff' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.12)' } }}>
        <SkipNextIcon sx={{ fontSize: 16 }} />
      </IconButton>

      {/* Scene indicator */}
      <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.3)', mx: 0.3 }}>
        {currentIdx + 1}/{scenes.length}
      </Typography>

      {/* Progress */}
      <Box sx={{ flex: 1, mx: 0.5 }}>
        <LinearProgress
          variant="determinate"
          value={(globalElapsed / Math.max(totalDur, 1)) * 100}
          sx={{
            height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 2, transition: 'transform 0.08s linear' },
          }}
        />
      </Box>

      {/* Time */}
      <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.3)', whiteSpace: 'nowrap' }}>
        {formatTime(globalElapsed)}/{formatTime(totalDur)}
      </Typography>

      {/* Mute */}
      <IconButton size="small" onClick={() => setMuted(m => !m)}
        sx={{ color: muted ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.45)', p: '4px', '&:hover': { color: '#fff' } }}>
        {muted ? <VolumeOffIcon sx={{ fontSize: 14 }} /> : <VolumeUpIcon sx={{ fontSize: 14 }} />}
      </IconButton>

      {/* Restart */}
      <IconButton size="small" onClick={() => goToScene(0)} sx={{ color: 'rgba(255,255,255,0.35)', p: '4px', '&:hover': { color: '#fff' } }}>
        <ReplayIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* Fullscreen */}
      <Tooltip title="Fullscreen (F)" arrow>
        <IconButton size="small" onClick={() => enterFullscreen()} sx={{ color: 'rgba(255,255,255,0.5)', p: '4px', '&:hover': { color: '#fff' } }}>
          <FullscreenIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        mt: 1,
        borderRadius: isFullscreen ? 0 : '10px',
        overflow: 'hidden',
        border: isFullscreen ? 'none' : '1px solid rgba(255,255,255,0.08)',
        bgcolor: '#08080f',
        boxShadow: isFullscreen ? 'none' : '0 4px 16px rgba(0,0,0,0.35)',
        cursor: isFullscreen && !fsControlsVisible ? 'none' : 'default',

        // ── COMPACT IN CHAT ──
        // Fixed small width so it doesn't fill the entire bubble
        width: isFullscreen ? '100%' : 340,
        maxWidth: isFullscreen ? '100%' : '100%',

        // Fullscreen CSS fallback
        ...(isFullscreen && {
          position: 'fixed', inset: 0, zIndex: 9999,
          borderRadius: 0, mt: 0, width: '100%',
        }),
      }}
      onMouseMove={isFullscreen ? showFsControls : undefined}
    >
      <style>{KEYFRAMES}</style>

      {/* ── Compact header (hidden in fullscreen — title shown in overlay) ── */}
      {!isFullscreen && (
        <Box sx={{
          px: 1.2, py: 0.7,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background: 'rgba(0,0,0,0.3)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <MovieIcon sx={{ fontSize: 12, color: accent }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)', letterSpacing: 0.2, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title || 'Video Lesson'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSpeaking && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', height: 12 }}>
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ width: 2.5, borderRadius: 2, background: accent, animation: `vpSpeakBar 0.7s ${i * 0.1}s ease-in-out infinite` }} />
                ))}
              </Box>
            )}
            <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
              {scenes.length} scenes · {formatTime(totalDur)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Canvas: 16:9 ratio ── */}
      <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
        <Box sx={{ position: 'absolute', inset: 0 }}>
          <ParticlesLayer accent={accent} running={isPlaying} />
          <SceneContent
            scene={scene}
            accent={accent}
            bg={bg}
            animKey={animKey}
            isSpeaking={isSpeaking}
            isFullscreen={isFullscreen}
          />
          {/* Top scene progress bar */}
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: isFullscreen ? 3 : 2, bgcolor: 'rgba(255,255,255,0.06)', zIndex: 5 }}>
            <Box sx={{
              height: '100%', bgcolor: accent, borderRadius: '0 2px 0 0',
              width: `${sceneProgress}%`, transition: 'width 0.08s linear',
              boxShadow: `0 0 6px ${accent}88`,
            }} />
          </Box>

          {/* YouTube-style fullscreen overlay */}
          {isFullscreen && (
            <FullscreenOverlay
              visible={fsControlsVisible}
              scenes={scenes}
              currentIdx={currentIdx}
              isPlaying={isPlaying}
              muted={muted}
              isSpeaking={isSpeaking}
              sceneProgress={sceneProgress}
              globalElapsed={globalElapsed}
              totalDur={totalDur}
              accent={accent}
              title={title}
              onTogglePlay={togglePlay}
              onPrev={() => goToScene(Math.max(0, currentIdx - 1))}
              onNext={() => goToScene(Math.min(scenes.length - 1, currentIdx + 1))}
              onMute={() => setMuted(m => !m)}
              onRestart={() => goToScene(0)}
              onExitFullscreen={exitFullscreen}
              onSceneClick={(i) => goToScene(i)}
            />
          )}

          {/* Click canvas to toggle controls visibility in fullscreen */}
          {isFullscreen && (
            <Box
              sx={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'default',pointerEvents: fsControlsVisible ? 'none' : 'auto', }}
              onClick={!fsControlsVisible ? showFsControls : undefined}
            />
          )}
        </Box>
      </Box>

      {/* ── Compact subtitle strip (chat mode only) ── */}
      {!isFullscreen && (
        <Box sx={{
          px: 1.5, py: 0.6, minHeight: 28, bgcolor: 'rgba(0,0,0,0.5)',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{
            fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.4,
            textAlign: 'center', overflow: 'hidden',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {scene?.voice || scene?.text.split('|')[0] || '—'}
          </Typography>
        </Box>
      )}

      {/* ── Compact scene dots (chat mode only) ── */}
      {!isFullscreen && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '4px', py: 0.6, bgcolor: '#08080f' }}>
          {scenes.map((_, i) => (
            <Box key={i} onClick={() => goToScene(i)} sx={{
              width: i === currentIdx ? 14 : 4, height: 4, borderRadius: 2, cursor: 'pointer',
              bgcolor: i === currentIdx ? accent : i < currentIdx ? `${accent}55` : 'rgba(255,255,255,0.15)',
              transition: 'all 0.22s ease',
              '&:hover': { bgcolor: i === currentIdx ? accent : 'rgba(255,255,255,0.35)' },
            }} />
          ))}
        </Box>
      )}

      {/* ── Mini controls (chat mode only) ── */}
      {!isFullscreen && MiniControls}

      {!supported && (
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,150,0,0.07)', borderTop: '1px solid rgba(255,150,0,0.12)' }}>
          <Typography sx={{ fontSize: 9, color: 'rgba(255,180,0,0.65)', textAlign: 'center' }}>
            Voice not supported in this browser
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AnimatedVideoPlayer;